import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { getTeamUuid } from '../../../../utils/cookies';
import { getResearchPipelines } from '../../../../Requests/ResearchPipelineRequests';
import { useMyContext } from '../../../../Components/ContextProviders/CreditsContextProvider';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import PipelineCard from '../../../../Components/ResearchPipeline/PipelineCard';
import { PIPELINE_STATUSES } from '../../../../utils/pipelineHelpers';
import ParticleLoader from "../../../../Components/ParticleLoader";

function ResearchPipeline() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { credits } = useMyContext();
    const [pipelines, setPipelines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadPipelines();
        
        // Set up polling for active pipelines
        const pollInterval = setInterval(() => {
            const activePipelines = pipelines.filter(pipeline => 
                [PIPELINE_STATUSES.PENDING, PIPELINE_STATUSES.PROCESSING].includes(pipeline.status)
            );
            
            if (activePipelines.length > 0) {
                loadPipelines(false); // Silent reload
            }
        }, 5000);

        return () => clearInterval(pollInterval);
    }, []);


    // Set document title when component mounts or translation changes
    useEffect(() => {
        document.title = `${t('ResearchPipeline.title')} - AIQDA`;
        return () => {
            // Reset to default title when leaving the component
            document.title = 'AIQDA';
        };
    }, [t('ResearchPipeline.title')]);

    const loadPipelines = async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            setError(null);
            
            const teamUuid = getTeamUuid();
            const response = await getResearchPipelines(teamUuid);
            
            if (response.data.success) {
                setPipelines(response.data.pipelines || []);
            } else {
                setError(t('ResearchPipeline.failedLoadPipelines'));
            }
        } catch (err) {
            console.error('Error loading research:', err);
            setError('Failed to load research projects');
        } finally {
            if (showLoading) setLoading(false);
        }
    };


    const handleCreatePipeline = (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        navigate('/research-pipeline/create');
    };

    const handlePipelineClick = (pipelineUuid) => {
        navigate(`/research-pipeline/${pipelineUuid}`);
    };


    if (loading) {
        return (
            <div className="md:pl-64 py-2 md:py-12 w-full flex justify-center items-center">
                <ParticleLoader size={48} />
            </div>
        );
    }

    return (
        <div className="md:pl-64 py-2 md:py-12 w-full flex flex-col gap-4 items-center px-4">
            <div className="flex flex-col w-full justify-center items-center gap-8 md:px-4">
                
                {/* Header Section */}
                <div className="bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl px-2">
                    <SectionHeader
                        title={t('ResearchPipeline.title')}
                        subtitle={t('ResearchPipeline.subtitle')}
                        breadcrubs={[{ name: t('ResearchPipeline.breadcrumb'), href: "/research-pipeline" }]}
                        actionTitle={t('ResearchPipeline.createPipeline')}
                        actionFunction={handleCreatePipeline}
                        isLoading={false}
                    />
                </div>

                {/* Beta Warning */}
                <div className="bg-amber-50 border border-amber-200 max-w-7xl rounded-2xl w-full shadow-md p-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg className="h-6 w-6 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-amber-800">
                                {t('ResearchPipeline.betaWarningTitle')}
                            </h3>
                            <div className="mt-1 text-sm text-amber-700">
                                <p>{t('ResearchPipeline.betaWarningMessage')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl p-6">
                    
                    {/* Section Header */}
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900">{t('ResearchPipeline.yourPipelines')}</h2>
                    </div>
                    

                    {/* Error State */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
                            <p>{error}</p>
                            <button
                                onClick={() => loadPipelines()}
                                className="text-red-800 underline hover:no-underline ml-2"
                            >
                                {t('ResearchPipeline.tryAgain')}
                            </button>
                        </div>
                    )}

                    {/* Pipeline List */}
                    {pipelines.length === 0 && !loading ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">📊</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {t('ResearchPipeline.noPipelinesYet')}
                            </h3>
                            <p className="text-gray-500 mb-6">
                                {t('ResearchPipeline.noPipelinesDescription')}
                            </p>
                            {pipelines.length === 0 && (
                                <button
                                    type="button"
                                    onClick={handleCreatePipeline}
                                    className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                                >
                                    {t('ResearchPipeline.createFirstPipeline')}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {pipelines.map((pipeline) => (
                                <PipelineCard
                                    key={pipeline.id}
                                    pipeline={pipeline}
                                    onClick={() => handlePipelineClick(pipeline.uuid)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ResearchPipeline;